<template>
  <div
    :class="[
      'vch-scroller',
      'vch-scroller--cards',
      isDrag ? 'vch-scroller--drag' : null,
    ]"
    ref="wrapper"
  >
    <button
      type="button"
      v-show="canAdvanceBackward"
      class="slider-navigation-button slider-navigation-prev"
      @click.prevent="navAction('prev')"
      @keypress="moveFocus('prev')"
      @touchstart.prevent="navAction('prev')"
      :aria-disabled="!canAdvanceBackward"
      :aria-describedby="ariaTitle"
    >
      <svg width="40" height="40" role="img" :aria-label="prevAriaLabel">
        <use href="/assets/sprite/icons.svg#btn-next" />
      </svg>
    </button>
    <div ref="scrollable" class="vch-scroller__scrollable" @mousedown="drag" tabindex="0">
      <slot />
    </div>
    <button
      type="button"
      v-show="canAdvanceForward"
      class="slider-navigation-button slider-navigation-next"
      @click.prevent="navAction('next')"
      @keypress="moveFocus('next')"
      @touchstart.prevent="navAction('next')"
      :aria-describedby="ariaTitle"
      :aria-disabled="!canAdvanceForward"
    >
      <svg width="40" height="40" role="img" :aria-label="nextAriaLabel">
        <use href="/assets/sprite/icons.svg#btn-next" />
      </svg>
    </button>
  </div>
</template>

<script>
import { globalResizeObserver } from 'Utils/';
import { setupCardImpressions } from 'Mixins/setupCardImpressions/setupCardImpressions';

const REF_SCROLLABLE = 'scrollable';

export default {
  name: 'VchCardScroller',
  mixins: [setupCardImpressions],
  props: {
    format: {
      type: String,
      default: 'medium',
    },
    cardType: {
      type: String,
    },
    ariaTitle: {
      type: String,
      default: '',
    },
     prevAriaLabel:{
      type: String,
      required: false,
      default: 'Previous'
    },
    nextAriaLabel:{
      type: String,
      required: false,
      default: 'Next'
    },
  },
  data() {
    return {
      currentCard: 0,
      isDrag: false,
      offset: 0,
      microMoveMargin: 15,
      observer: null,
      observerTab: null,
      cards: [],
      canAdvanceForward: false,
      rtlEnabled: false,
    };
  },
  computed: {
    canAdvanceBackward() {
      return this.currentCard > 0;
    },
  },
  mounted() {
    this.rtlEnabled = document.body.classList.contains('rtl-content');
    this.cards =
      this.$slots.default?.[0]?.children
        ?.filter((c) => c.tag === 'li')
        ?.map((c) => c.elm) || [];
    this.setActiveClass();
    this.setTabIndex();
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    // desktop view
    const cardLength = this.format == 'articles' ? 3 : 4;
    if (mediaQuery.matches && this.cards.length > cardLength) {
      this.canAdvanceForward = true;
    }
    // mobile view
    if (!mediaQuery.matches && this.cards.length > 1) {
      this.canAdvanceForward = true;
    }

    globalResizeObserver.observe(
      this.$refs[REF_SCROLLABLE],
      this.setSizes,
      200
    );

    document.addEventListener('keyup', this.onKeyUp);

    if (this.cardType === 'product') {
      this.setupCardImpressions();
    }
  },
  beforeDestroy() {
    globalResizeObserver.unobserve(this.$refs[REF_SCROLLABLE]);
    document.removeEventListener('keyup', this.onKeyUp);
    this.observer.disconnect();
  },
  methods: {
    onKeyUp(event) {
      const elementIsFocused = this.$refs['wrapper'].matches(':focus-within');
      if (this.radioScroller || !elementIsFocused) return;
      if (event.keyCode === 37) {
        this.navAction('prev');
      } else if (event.keyCode === 39) {
        this.navAction('next');
      }
    },
    navAction(direction) {
  let nextCard = this.currentCard;
  const screenWidth = window.matchMedia('(min-width: 768px)');
  const step = screenWidth.matches ? (this.rtlEnabled ? 3 : 1) : 1;
  
  if (direction === 'next') {
    nextCard += this.rtlEnabled ? ((this.currentCard === 0) ? step : 1) : 1;
  } else {
    nextCard -= this.rtlEnabled ? ((this.currentCard === 3) ? step : 1) : 1;
  }
  this.scrollToCard(nextCard);
},
    setSizes() {
      const { scrollWidth, clientWidth } = this.$refs[REF_SCROLLABLE];
      this.offset = scrollWidth - clientWidth;
    },
    drag(e) {
      const pos = {
        left: this.$refs[REF_SCROLLABLE].scrollLeft,
        x: e.clientX,
      };
      const move = (e) => {
        e.preventDefault();
        let dx = e.clientX - pos.x;
        this.isDrag = this.isDrag || Math.abs(dx) > this.microMoveMargin;
        this.$refs[REF_SCROLLABLE].scrollLeft = pos.left - dx;
      };
      const release = () => {
        const currentScrollAmount = this.$refs[REF_SCROLLABLE].scrollLeft;
        let closetCard = 0;
        let closestCardOffset = currentScrollAmount - this.cards[0].offsetLeft;
        for (let i = 1; i < this.cards.length; i++) {
          const distanceToCard = Math.abs(
            currentScrollAmount - this.cards[i].offsetLeft
          );
          if (distanceToCard < closestCardOffset) {
            closetCard = i;
            closestCardOffset = distanceToCard;
          }
        }

        // Check if we are at full scroll width and need to push to see the last card
        if (
          Math.abs(currentScrollAmount - this.offset) < 10 &&
          this.cards[closetCard].offsetLeft - currentScrollAmount < 0
        ) {
          closetCard++;
        }

        this.currentCard = closetCard;
        this.scrollToCard(closetCard);

        this.isDrag = false;
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', release);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', release);
    },
    scrollToCard(cardIndex) {
      if (!this.cards.length) return;
      this.currentCard = cardIndex;

      if (this.currentCard > this.cards.length - 1) {
        this.currentCard = this.cards.length - 1;
      } else if (this.currentCard < 0) {
        this.currentCard = 0;
      }

      const nextScrollPos = this.cards[this.currentCard].offsetLeft;
      this.$refs[REF_SCROLLABLE].scrollTo({
        left: nextScrollPos,
        behavior: 'smooth',
      });
      this.canAdvanceForward = Math.abs(nextScrollPos) < (this.rtlEnabled && this.currentCard === 0 ? this.offset * 10 : this.offset);
    },
    moveFocus(dir) {
      const vchLinkClass = '.vch-card__link';
      if (dir == 'next') {
        setTimeout(() => {
          const lastFocusElement = this.cards
            .filter((item) => {
              if (item.querySelector('[tabindex="0"]')) {
                return item.querySelector('[tabindex="0"]');
              }
            })
            .slice(-1)[0];
          if (lastFocusElement) {
            lastFocusElement.querySelector(vchLinkClass).focus();
          }
        }, 500);
      } else {
        setTimeout(() => {
          const firstFocusElement = this.cards.filter((item) => {
            if (item.querySelector('[tabindex="0"]')) {
              return item.querySelector('[tabindex="0"]');
            }
          })[0];
          if (
            firstFocusElement &&
            firstFocusElement.querySelector(vchLinkClass)
          ) {
            firstFocusElement.querySelector(vchLinkClass).focus();
          }
        }, 500);
      }
    },
    setActiveClass() {
      let callback = (entries) => {
        entries.forEach((entry) => {
          !entry.isIntersecting
            ? entry.target.classList.add('--hide')
            : entry.target.classList.remove('--hide');
        });
      };
      this.observer = new IntersectionObserver(callback, {
        threshold: [0.01],
      });
      for (let card of this.cards) {
        this.observer.observe(card);
      }
    },
    setTabIndex() {
      let callback = (entries) => {
        entries.forEach((entry) => {
          if (entry.target.querySelector('.vch-card__link') && (this.canAdvanceBackward || this.canAdvanceForward) ) {
            !entry.isIntersecting
              ? entry.target
                  .querySelector('.vch-card__link')
                  .setAttribute('tabindex', -1)
              : entry.target
                  .querySelector('.vch-card__link')
                  .setAttribute('tabindex', 0);
          }
        });
      };
      this.observerTab = new IntersectionObserver(callback, {
        threshold: [0.5],
      });
      for (let card of this.cards) {
        this.observerTab.observe(card);
      }
    },
  },
};
</script>
